/**
 * Extract final output from trace output JSON
 * Looks for the last meaningful message in the conversation
 * Only returns data for AGENT, CHAIN, or LLM span kinds
 */
export function extractFinalOutput(outputData) {
  // Handle null, undefined, or missing data
  if (!outputData) return null;
  if (typeof outputData !== 'object') return null;
  if (!outputData.text || outputData.text.trim() === '') return null;

  // Only show output for AGENT, CHAIN, or LLM spans
  if (outputData.span_kind !== 'AGENT' && outputData.span_kind !== 'CHAIN' && outputData.span_kind !== 'LLM') {
    return null;
  }

  try {
    const parsed = JSON.parse(outputData.text);

    // Format 1: OpenAI Completion format - {"choices": [{"message": {...}}]}
    if (parsed.choices && Array.isArray(parsed.choices) && parsed.choices.length > 0) {
      const firstChoice = parsed.choices[0];
      if (firstChoice.message) {
        let output = '';

        // Check for text content
        if (firstChoice.message.content && firstChoice.message.content.trim().length > 0) {
          output = firstChoice.message.content;
        }

        // Check for tool_calls (may exist alongside content)
        if (firstChoice.message.tool_calls && Array.isArray(firstChoice.message.tool_calls)) {
          const toolCallSummary = firstChoice.message.tool_calls
            .map(tc => `${tc.function?.name || 'function'}()`)
            .join(', ');
          const toolCallsText = `[Tool calls: ${toolCallSummary}]`;
          output = output ? `${output}\n\n${toolCallsText}` : toolCallsText;
        }

        // Return combined content + tool_calls, or nothing if both empty
        if (output) return output;
      }
    }

    // Format 2: CrewAI format - {"raw": "...", "tasks_output": [...]}
    if (parsed.raw && typeof parsed.raw === 'string') {
      return parsed.raw;
    }

    // Format 3a: Gemini API format - {"content": {"parts": [{"text": "..."}]}}
    if (parsed.content && parsed.content.parts && Array.isArray(parsed.content.parts)) {
      // Find first part with text content
      for (const part of parsed.content.parts) {
        if (part.text && part.text.trim().length > 0) {
          return part.text;
        }
      }
    }

    // Format 3b: Google ADK / Gemini contents format - {"contents": [{role: "model", parts: [{text: "..."}]}, ...]}
    if (parsed.contents && Array.isArray(parsed.contents) && parsed.contents.length > 0) {
      // Find the last model message
      const modelMessages = parsed.contents.filter(c => c.role === 'model');
      const lastModelMsg = modelMessages[modelMessages.length - 1];
      if (lastModelMsg?.parts && Array.isArray(lastModelMsg.parts)) {
        // Extract text from parts, filter out function_call parts
        const textPart = lastModelMsg.parts.find(p => p.text && !p.function_call);
        if (textPart?.text) return textPart.text;
      }
    }

    // Format 4: LangChain/LangGraph - {"messages": [...]}
    if (parsed.messages && Array.isArray(parsed.messages) && parsed.messages.length > 0) {
      // Find the last AI/assistant message with actual content (final response)
      for (let i = parsed.messages.length - 1; i >= 0; i--) {
        const message = parsed.messages[i];

        // Object format
        if (message && typeof message === 'object') {
          // Look for AI/assistant messages with content
          if ((message.type === 'ai' || message.role === 'assistant') && message.content) {
            // Handle array content (Anthropic format with text blocks)
            if (Array.isArray(message.content)) {
              // Find text content (skip tool_use blocks)
              const textContent = message.content
                .filter(item => item.type === 'text' && item.text)
                .map(item => item.text)
                .join('\n');
              if (textContent.trim().length > 0) {
                return textContent;
              }
            }
            // Handle string content
            else if (typeof message.content === 'string' && message.content.trim().length > 0) {
              return message.content;
            }
          }

          // Fallback: any message with content
          if (message.content && typeof message.content === 'string' && message.content.trim().length > 0) {
            return message.content;
          }
        }

        // Array format: ["role", "content"]
        if (Array.isArray(message) && message.length >= 2 && message[1]) {
          return message[1];
        }
      }
    }

    // Format 5: Direct object with content field (simple format)
    if (parsed.content && typeof parsed.content === 'string') {
      return parsed.content;
    }

    // Format 6: Direct string
    if (typeof parsed === 'string') {
      return parsed;
    }

    // Fallback: return original
    return outputData.text;
  } catch (e) {
    // Not JSON or parsing failed, return as-is if not empty
    return outputData.text.trim() || null;
  }
}

/**
 * Truncate text to specified length with ellipsis
 */
export function truncateText(text, maxLength = 100) {
  // Handle null, undefined, or non-string values
  if (!text) return 'N/A';
  if (typeof text !== 'string') return 'N/A';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}
